import { Card } from "@rumbledirect/ui";
import { Col, Row, Statistic, Typography, Tag } from "antd";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { RiseOutlined, FallOutlined } from "@ant-design/icons";
import { insightsAreaData } from "./data/insightsAreaData";
import { Table } from "@/components/v2";

const { Text } = Typography;

const ClaimsAreaTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]; // using first series (uv)
  const value: number = point?.value ?? 0;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e5e5",
        padding: 8,
        borderRadius: 4,
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 14 }}>
        Kosten:{" "}
        <strong>
          {new Intl.NumberFormat("nl-NL", {
            style: "currency",
            currency: "EUR",
          }).format(value)}
        </strong>
      </div>
    </div>
  );
};

const repairNetworkData = [
  {
    name: "Autoschade Herstel Groep",
    avgCost: "2.850,98",
    avgDuration: "11,7 dagen",
    handled: 145,
    rating: "Efficient",
    savings: 45000,
  },
  {
    name: "AAS Schadeherstel",
    avgCost: "3.294,11",
    avgDuration: "13,8 dagen",
    handled: 89,
    rating: "Average",
    savings: 12000,
  },
  {
    name: "ABS Autoherstel",
    avgCost: "1.736,51",
    avgDuration: "17,2 dagen",
    handled: 67,
    rating: "Expensive",
    savings: -78000,
  },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(value);

export const ClaimsInsights = () => {
  return (
    <Row gutter={[16, 32]} align="stretch">
      <Col span={8}>
        <Card title="Schade- en reparatiekosten">
          <Row gutter={16}>
            <Col span={12}>
              <Statistic
                title={
                  <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    Budget
                  </span>
                }
                value="€2.450.000"
                valueStyle={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#262626",
                }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title={
                  <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    Huidig
                  </span>
                }
                value="€2.680.000"
                valueStyle={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#262626",
                }}
              />
            </Col>
          </Row>
          <div style={{ marginTop: "20px" }}>
            <Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
              Afwijking
            </Text>
            <div style={{ marginTop: "4px" }}>
              <Tag color="red" style={{ margin: 0, padding: "4px 8px" }}>
                <RiseOutlined style={{ marginRight: "4px" }} />
                +9.4% (€230.000)
              </Tag>
            </div>
          </div>
        </Card>
      </Col>
      <Col span={8}>
        <Card title="Interne afhandelingskosten">
          <Row gutter={16}>
            <Col span={12}>
              <Statistic
                title={
                  <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    Doel
                  </span>
                }
                value="€150 per claim"
                valueStyle={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#262626",
                }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title={
                  <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    Huidig
                  </span>
                }
                value="€134 per claim"
                valueStyle={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#262626",
                }}
              />
            </Col>
          </Row>
          <div style={{ marginTop: "20px" }}>
            <Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
              Afwijking
            </Text>
            <div style={{ marginTop: "4px" }}>
              <Tag color="green" style={{ margin: 0, padding: "4px 8px" }}>
                <FallOutlined style={{ marginRight: "4px" }} />
                -10,7% (€16)
              </Tag>
            </div>
          </div>
        </Card>
      </Col>
      <Col span={8}>
        <Card title="Gemiddelde doorlooptijd">
          <Row gutter={16}>
            <Col span={12}>
              <Statistic
                title={
                  <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    Doel
                  </span>
                }
                value="14 dagen"
                valueStyle={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#262626",
                }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title={
                  <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    Huidig
                  </span>
                }
                value="12,2 dagen"
                valueStyle={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#262626",
                }}
              />
            </Col>
          </Row>
          <div style={{ marginTop: "20px" }}>
            <Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
              Afwijking
            </Text>
            <div style={{ marginTop: "4px" }}>
              <Tag color="green" style={{ margin: 0, padding: "4px 8px" }}>
                <FallOutlined style={{ marginRight: "4px" }} />
                -12,9% (1,8 dagen)
              </Tag>
            </div>
          </div>
        </Card>
      </Col>
      <Col span={24}>
        <Card
          title="Prestaties herstelnetwerk"
          variant="borderless"
          style={{ height: "100%" }}
        >
          <Table
            dataSource={repairNetworkData}
            pagination={false}
            size="small"
            style={{ fontSize: "12px" }}
            columns={[
              {
                title: "Netwerk",
                dataIndex: "name",
                key: "name",
                width: "30%",
                render: (text) => (
                  <Text style={{ fontSize: "12px" }}>{text}</Text>
                ),
              },
              {
                title: "Gemiddelde kosten",
                dataIndex: "avgCost",
                key: "avgCost",
                render: (v) => (
                  <Text style={{ fontSize: "12px", fontWeight: 500 }}>
                    €{v}
                  </Text>
                ),
                width: "20%",
              },
              {
                title: "Gemiddelde doorlooptijd",
                dataIndex: "avgDuration",
                key: "avgDuration",
                render: (v) => (
                  <Text style={{ fontSize: "12px", fontWeight: 500 }}>{v}</Text>
                ),
                width: "20%",
              },
              {
                title: "Aantal reparaties",
                dataIndex: "handled",
                key: "handled",
                width: "20%",
                render: (v) => (
                  <Text style={{ fontSize: "12px", fontWeight: 500 }}>{v}</Text>
                ),
              },
            ]}
          />
        </Card>
      </Col>
      <Col span={24}>
        <Card title="Gem. kosten per claim per maand">
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                width={500}
                height={400}
                data={insightsAreaData}
                margin={{
                  top: 10,
                  right: 30,
                  left: 20,
                  bottom: 0,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip content={<ClaimsAreaTooltip />} />
                <Area
                  type="monotone"
                  dataKey="uv"
                  stroke="#F76B0E"
                  fill="#F76B0E"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </Col>
    </Row>
  );
};
